import { API_BASE_URL } from './apiConfig';

const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
const ACTIVITY_CHECK_INTERVAL_MS = 30 * 1000;

let refreshPromise = null;
let lastActivityCheck = 0;

function decodePayload(token) {
  try {
    const part = token.split('.')[1];
    if (!part) {
      return null;
    }
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(padded), (char) => char.charCodeAt(0))
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getTokenExpiry(token) {
  const payload = decodePayload(token);
  return payload?.exp ? payload.exp * 1000 : null;
}

export function isTokenExpired(token) {
  const expiry = getTokenExpiry(token);
  return expiry !== null && expiry <= Date.now();
}

export function isTokenExpiringSoon(token, threshold = REFRESH_THRESHOLD_MS) {
  const expiry = getTokenExpiry(token);
  return expiry !== null && expiry - Date.now() <= threshold;
}

export async function refreshAuthToken() {
  const token = localStorage.getItem('authToken');
  if (!token || isTokenExpired(token)) {
    return null;
  }
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = fetch(`${API_BASE_URL}/auth/refresh-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ token }),
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }
      const data = await response.json();
      if (!data?.token) {
        throw new Error('Token refresh returned no token');
      }
      localStorage.setItem('authToken', data.token);
      if (data.userId) {
        localStorage.setItem('userId', data.userId);
      }
      if (data.role) {
        localStorage.setItem('userRole', data.role);
      }
      if (data.email) {
        localStorage.setItem('userEmail', data.email);
      }
      if (data.fullName) {
        localStorage.setItem('fullName', data.fullName);
      }
      if (data.onboardingCompleted !== undefined) {
        localStorage.setItem('onboardingComplete', String(data.onboardingCompleted));
      }
      if (data.plan) {
        localStorage.setItem('plan', data.plan);
      }
      return data.token;
    })
    .catch((error) => {
      console.warn('Could not refresh JWT token:', error.message);
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export async function ensureFreshToken() {
  const token = localStorage.getItem('authToken');
  if (!token || isTokenExpired(token)) {
    return token;
  }
  if (!isTokenExpiringSoon(token)) {
    return token;
  }
  const refreshed = await refreshAuthToken();
  return refreshed || token;
}

export function startTokenRefreshWatcher() {
  const onActivity = () => {
    const now = Date.now();
    if (now - lastActivityCheck < ACTIVITY_CHECK_INTERVAL_MS) {
      return;
    }
    lastActivityCheck = now;
    if (localStorage.getItem('authToken')) {
      void ensureFreshToken();
    }
  };

  const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach((event) => window.addEventListener(event, onActivity, { passive: true }));

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      onActivity();
      void ensureFreshToken();
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    events.forEach((event) => window.removeEventListener(event, onActivity));
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
